import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CircleCheck, CircleAlert, Download, RefreshCw, X } from 'lucide-react';
import {
    getActiveListingSync,
    retryListingSync,
    dismissListingSync,
    downloadListingSyncReport,
} from './tradeInMockApi.js';

// ERP-5289 — banner on Trade-In Listing after an Excel import is saved: listing items are
// created / updated / deleted in the ERP and pushed to WooCommerce. Three states, as in the
// design: amber while pushing (done / total bars), green once everything synced, red when
// some rows failed (per-action Success / Failed, Try Again while attempts remain).
// ERP-5315: state comes from the server (getActiveListingSync), polled every 3 s while pushing.

const POLL_MS = 3000;
const FONT = 'Montserrat, sans-serif';
const fmt = (n) => Number(n || 0).toLocaleString('en-US');

const THEME = {
    run: { bg: '#FFF5E0', border: '#CC9A06', accent: '#CC9A06' },
    done: { bg: '#D1E7DD', border: '#146C43', accent: '#146C43' },
    error: { bg: '#FFE9EC', border: '#F1AEB5', accent: '#B02A37' },
};
const GREEN = '#146C43';
const RED = '#B02A37';
const GREY = '#6C757D';
const LINE = '#ADB5BD';

const Divider = () => <span style={{ width: 0, height: 15, borderLeft: `1px solid ${LINE}`, flexShrink: 0 }} />;

const Dot = ({ color }) => <span style={{ width: 5, height: 5, borderRadius: 99, background: color, flexShrink: 0 }} />;

function Bar({ done, total, color }) {
    const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 100;
    return (
        <span style={{ width: 85, height: 8, borderRadius: 99, background: '#CED4DA', overflow: 'hidden', flexShrink: 0 }}>
            <span style={{ display: 'block', height: 8, width: `${pct}%`, borderRadius: 99, background: color, transition: 'width .3s ease' }} />
        </span>
    );
}

function SmallButton({ icon, children, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                padding: '0 10px', boxSizing: 'border-box', background: '#FFFFFF', border: '1px solid #212529',
                borderRadius: 8, fontFamily: FONT, fontSize: 9, fontWeight: 600, color: '#212529', cursor: 'pointer',
            }}
        >
            {icon}{children}
        </button>
    );
}

// A row of segments separated by hairlines, wrapping onto further lines when it runs out of room.
function Segments({ children }) {
    const items = React.Children.toArray(children).filter(Boolean);
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 15, rowGap: 10 }}>
            {items.map((child, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                    {i > 0 && <Divider />}
                    {child}
                </div>
            ))}
        </div>
    );
}

export default function TradeInSyncBanner({ onSettled }) {
    const [sync, setSync] = useState(null);
    // Read through a ref so an inline callback from the page doesn't restart the poll timer.
    const onSettledRef = useRef(onSettled);
    useEffect(() => { onSettledRef.current = onSettled; }, [onSettled]);

    const load = useCallback(async () => {
        const next = await getActiveListingSync();
        setSync(next);
        return next;
    }, []);

    useEffect(() => { load(); }, [load]);

    // Poll only while the push is running.
    useEffect(() => {
        if (!sync || sync.phase !== 'run') return undefined;
        const t = setTimeout(async () => {
            const next = await load();
            // Lets the page refetch its table once the rows have actually changed.
            if (next && next.phase !== 'run' && onSettledRef.current) onSettledRef.current(next);
        }, POLL_MS);
        return () => clearTimeout(t);
    }, [sync, load]);

    if (!sync) return null;

    const t = THEME[sync.phase];
    const close = async () => { await dismissListingSync(sync.id); setSync(null); };
    const retry = async () => { await retryListingSync(sync.id); load(); };
    const report = () => downloadListingSyncReport(sync.id);

    let icon;
    let title;
    if (sync.phase === 'run') {
        icon = <span style={{ width: 24, height: 24, borderRadius: '50%', border: '3px solid #E9ECEF', borderTopColor: t.accent, animation: 'tiSyncSpin 0.9s linear infinite', boxSizing: 'border-box', flexShrink: 0 }} />;
        title = <span style={{ fontSize: 12, fontWeight: 700 }}>Creating and Syncing Listing Items to WooCommerce</span>;
    } else if (sync.phase === 'done') {
        icon = <CircleCheck size={24} color="#0A3622" style={{ flexShrink: 0 }} />;
        title = (
            <Segments>
                <span style={{ fontSize: 12, fontWeight: 700 }}>Creation and Sync of Listing Items to WooCommerce Completed</span>
                <span style={{ fontSize: 12 }}>
                    <span style={{ fontWeight: 700, color: GREEN }}>{fmt(sync.total)}</span>
                    <span style={{ color: GREY }}>&nbsp; Listing Items synced successfully</span>
                </span>
            </Segments>
        );
    } else {
        icon = <CircleAlert size={24} color="#DC3545" style={{ flexShrink: 0 }} />;
        title = (
            <Segments>
                <span style={{ fontSize: 12, fontWeight: 700 }}>Creation and Sync of Listing Items to WooCommerce Completed with issues</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, fontWeight: 700 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Dot color={GREEN} /><span style={{ color: GREEN }}>{fmt(sync.ok)}</span>Success</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Dot color={RED} /><span style={{ color: RED }}>{fmt(sync.fail)}</span>Failed</span>
                </span>
            </Segments>
        );
    }

    const groupCell = (g) => {
        if (sync.phase === 'error') {
            return (
                <span style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, whiteSpace: 'nowrap' }}>
                    <span style={{ fontWeight: 700 }}>{g.label}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontWeight: 500 }}><Dot color={GREEN} /><span style={{ color: GREEN }}>{fmt(g.ok)}</span><span style={{ color: GREY }}>Success</span></span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontWeight: 500 }}><Dot color={RED} /><span style={{ color: RED }}>{fmt(g.fail)}</span><span style={{ color: GREY }}>Failed</span></span>
                </span>
            );
        }
        return (
            <span style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, whiteSpace: 'nowrap' }}>
                <span style={{ fontWeight: 700 }}>{sync.phase === 'run' ? g.runLabel : g.label}</span>
                <span style={{ fontWeight: 500 }}><span style={{ color: t.accent }}>{fmt(g.done)}</span><span style={{ color: GREY }}> / {fmt(g.total)}</span></span>
                <Bar done={g.done} total={g.total} color={t.accent} />
            </span>
        );
    };

    return (
        <div
            role="status"
            style={{
                display: 'flex', flexDirection: 'column', gap: 12, padding: 15, marginBottom: 10, boxSizing: 'border-box',
                background: t.bg, border: `1px solid ${t.border}`, borderRadius: 12, fontFamily: FONT, color: '#212529',
            }}
        >
            <style>{'@keyframes tiSyncSpin { to { transform: rotate(360deg); } }'}</style>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 26 }}>
                {icon}
                <div style={{ flex: 1, minWidth: 0 }}>{title}</div>
                {sync.phase !== 'run' && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 15 }}>
                            <SmallButton icon={<Download size={12} />} onClick={report}>Report</SmallButton>
                            {sync.phase === 'error' && sync.attemptsRemaining > 0 && (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                                    <SmallButton icon={<RefreshCw size={12} />} onClick={retry}>Try Again</SmallButton>
                                    <span style={{ fontSize: 8, fontWeight: 500, color: GREY, whiteSpace: 'nowrap' }}>
                                        {sync.attemptsRemaining} attempt{sync.attemptsRemaining === 1 ? '' : 's'} remaining
                                    </span>
                                </div>
                            )}
                        </div>
                        <button type="button" onClick={close} title="Close" style={{ display: 'flex', padding: 0, border: 'none', background: 'none', cursor: 'pointer', color: '#212529' }}>
                            <X size={18} />
                        </button>
                    </div>
                )}
            </div>
            <div style={{ height: 0, borderBottom: `1px solid ${LINE}` }} />
            <Segments>{sync.groups.map(g => <React.Fragment key={g.key}>{groupCell(g)}</React.Fragment>)}</Segments>
        </div>
    );
}
